import { useQuery } from "react-query";
import { getProcessStatus } from "api/process";
import { useEffect } from "react";
import { GET_PROCESS_STATUS } from "constants/reactQueryKeys";

/**
 * Custom hook used in GlobalMiraProvider to poll the process status at regular intervals.
 * Polling stops when the status is either "completed" or "failed."
 *
 * @category Hooks
 * @param {Object} options - Hook options
 * @param {string} options.invocationId - The ID of the process to check status for.
 * @param {Function} options.onSuccess - Callback function triggered when the request succeeds.
 * @param {Function} options.onError - Callback function triggered when the request fails.
 * @returns {Object} Query result object containing:
 * - `pollingError` {Error | null} - Error object if the request fails.
 * - `data` {Object | undefined} - Latest polling response data containing process status.
 */
const useProcessStatusPolling = ({ invocationId, onSuccess, onError }) => {
  const { data, error } = useQuery(
    [GET_PROCESS_STATUS, invocationId],
    () => getProcessStatus(invocationId),
    {
      enabled: !!invocationId,
      refetchInterval: (data) =>
        data?.data?.status === "completed" || data?.data?.status === "failed" ? false : 3000, // Poll every 3 seconds while waiting for results
      onSuccess,
    }
  );

  useEffect(() => {
    if (error) {
      onError(error);
    }
  }, [error, onError]);

  return { pollingError: error, data };
};

export default useProcessStatusPolling;
